import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { makeStyles } from '@material-ui/core'
import { useQueryClient } from 'react-query'
import { useTranslate } from 'react-admin'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import calendarLiveStreamsStyles from './calendarLiveStreams.styles'
import CalendarLiveStreamRow from './CalendarLiveStreamRow'
import useLiveStreams from '../../../hooks/useLiveStreams'
import useQueryParams from '../../../hooks/useQueryParams'
import QueryKeys from '../../../utils/constants/queryKeys'
import BROADCAST_STATES from '../../../utils/constants/broadcastStates'
import { USER_ADDONS } from '../../../utils/constants/userAddons'
import isUserAddonsValid from '../../../utils/helpers/isUserAddonsValid'
import USER_PERMISSIONS from '../../../utils/constants/userPermissions'
import hasPermission from '../../../utils/helpers/hasPermission'

const LIVE_STATES = [BROADCAST_STATES.running, BROADCAST_STATES.paused]

// Hoisted rather than built inside the component: the section re-renders on every update,
// and a fresh stylesheet each time would restart the pulse and spinner animations.
const useStyles = makeStyles(calendarLiveStreamsStyles)

const CalendarLiveStreams = () => {
  const [expanded, setExpanded] = useState(false)
  const [confirmingStopId, setConfirmingStopId] = useState(null)
  const translate = useTranslate()
  const queryClient = useQueryClient()
  const runningBroadcastIdsRef = useRef()
  const { org } = useQueryParams()
  const classes = useStyles()

  // Pausing, resuming and stopping a stream is for whoever may update events, on an org that has
  // the Livestream addon — the same gate the floating banner's controls use. Nobody else sees
  // the section, so nobody else follows it either.
  const canControlStreams =
    hasPermission(USER_PERMISSIONS.UPDATE_EVENT) &&
    isUserAddonsValid([USER_ADDONS.LIVESTREAM])

  // The org's live streams over its hub, kept current as they change.
  const { data } = useLiveStreams(org, Boolean(canControlStreams && org))

  // Refetch open event views only when a broadcast actually starts or stops, not on every update of
  // the list. Tracked per org so switching orgs doesn't compare unrelated lists.
  useEffect(() => {
    if (!data) return
    const runningBroadcastIds = JSON.stringify(data.map((event) => event?.id).sort())
    const previous = runningBroadcastIdsRef.current
    if (previous && previous.org === org && previous.ids !== runningBroadcastIds) {
      queryClient.invalidateQueries(QueryKeys.GET_EVENT).then(() => {})
    }
    runningBroadcastIdsRef.current = { org, ids: runningBroadcastIds }
  }, [data, org, queryClient])

  const streams = data || []

  if (!canControlStreams || streams.length === 0) return null

  const liveCount = streams.filter(({ broadcast }) => LIVE_STATES.includes(broadcast?.state)).length
  // A stream that is still being created is not "live" yet, but its row is already there —
  // count it rather than announce "0 streams live" for the second it takes to come up.
  const count = liveCount || streams.length

  const handleToggle = () => {
    setExpanded(!expanded)
    setConfirmingStopId(null)
  }

  return (
    <section className={classes.section}>
      <button
        type='button'
        className={classes.summary}
        onClick={handleToggle}
        aria-expanded={expanded}
      >
        <span className={classNames(classes.dot, classes.dotLive)} />
        <span className={classes.summaryLabel}>
          {count === 1
            ? translate('ra.text.oneStreamLive')
            : translate('ra.text.streamsLive', { count })}
        </span>
        <span className={classes.spacer} />
        <span className={classes.toggleLabel}>
          {expanded ? translate('ra.buttons.hideSmall') : translate('ra.buttons.showSmall')}
        </span>
        {expanded ? (
          <KeyboardArrowUpIcon className={classes.chevron} />
        ) : (
          <KeyboardArrowDownIcon className={classes.chevron} />
        )}
      </button>

      {expanded && (
        <div className={classes.rows}>
          {streams.map((broadcastEvent) => (
            <CalendarLiveStreamRow
              key={broadcastEvent?.id}
              broadcastEvent={broadcastEvent}
              isConfirmingStop={confirmingStopId === broadcastEvent?.id}
              onRequestStop={() => setConfirmingStopId(broadcastEvent?.id)}
              onCancelStop={() => setConfirmingStopId(null)}
            />
          ))}
        </div>
      )}
    </section>
  )
}

export default CalendarLiveStreams
